import React, { useEffect } from 'react';
import { getControlId } from '@veeva-ctv/utils';
import { FormikProps } from 'formik';
import { useStyles as useStylesAutocomplete } from '@/hooks/styles/autocomplete';
import { useLazyQuery } from '@apollo/client';
import { GENERAL_SEARCH, LOCATION_SUGGESTIONS, LOCATION_COORDINATES_BY_KEY } from '@/queries';
import { Grid, Autocomplete, TextField } from '@mui/material';
import { UIOnlyFields } from '@/types';
import {
  SiteApplicationInput,
  SearchType,
  GeneralSearchQuery,
  GeneralSearchQueryVariables,
  LocationSuggestionsQuery,
  LocationSuggestionsQueryVariables,
  LocationCoordinatesByKeyQuery,
  LocationCoordinatesByKeyQueryVariables,
  LocationSuggestionType,
} from '@/gql/graphql';

interface StateProvinceInputProps {
  formik: FormikProps<UIOnlyFields & SiteApplicationInput>;
  countryFieldName: 'country' | 'smoCountry';
  stateFieldName: 'stateProvince' | 'smoState';
  cityFieldName: 'city' | 'smoCity';
  addressFieldName: 'address1' | 'smoAddress1';
  postalCodeFieldName: 'postalCode' | 'smoZip';
  countryFieldLabel?: string;
  stateFieldLabel?: string;
  cityFieldLabel?: string;
  addressFieldLabel?: string;
  postalCodeFieldLabel?: string;
}

const StandardCountryStateProvinceInput = ({
  formik,
  countryFieldName,
  stateFieldName,
  cityFieldName,
  addressFieldName,
  postalCodeFieldName,
  countryFieldLabel = 'Country*',
  stateFieldLabel = 'State/Province*',
  cityFieldLabel = 'City*',
  addressFieldLabel = 'Address*',
  postalCodeFieldLabel = 'Zip/Postal code*',
}: StateProvinceInputProps) => {
  const classesAutocomplete = useStylesAutocomplete();

  const [searchCountry, { data: countryData, loading: countryLoading }] = useLazyQuery<
    GeneralSearchQuery,
    GeneralSearchQueryVariables
  >(GENERAL_SEARCH);

  const [searchState, { data: stateData, loading: stateLoading }] = useLazyQuery<
    GeneralSearchQuery,
    GeneralSearchQueryVariables
  >(GENERAL_SEARCH);

  const [getSuggestions, { data: suggestionsData, loading: suggestionsLoading }] = useLazyQuery<
    LocationSuggestionsQuery,
    LocationSuggestionsQueryVariables
  >(LOCATION_SUGGESTIONS);

  const [getLocation] = useLazyQuery<
    LocationCoordinatesByKeyQuery,
    LocationCoordinatesByKeyQueryVariables
  >(LOCATION_COORDINATES_BY_KEY);

  const country = formik.values[countryFieldName];
  const stateProvince = formik.values[stateFieldName];
  const city = formik.values[cityFieldName];
  const address = formik.values[addressFieldName];
  const postalCode = formik.values[postalCodeFieldName];
  const addressAutocompleteLabel = formik.values.addressAutocompleteLabel;
  const addressAutocompleteKey = formik.values.addressAutocompleteKey;

  const getActiveSuggestion = () => {
    if (addressAutocompleteLabel && addressAutocompleteKey) {
      return {
        key: addressAutocompleteKey,
        text: addressAutocompleteLabel,
      };
    }

    return null;
  };

  const activeCountry =
    (countryData && countryData?.generalSearch.items.find((elem) => elem?.name === country)) ||
    null;
  const activeState =
    (stateData && stateData.generalSearch.items.find((elem) => elem.name === stateProvince)) ||
    null;
  const activeSuggestion = getActiveSuggestion();

  // Prepouplate country dropdown with default ranking
  useEffect(() => {
    void searchCountry({
      variables: {
        input: {
          generalSearchText: '',
          filterText: '',
          generalSearchType: SearchType.Country,
        },
      },
    });
  }, []);

  // Change postal stateProvince autocomplete options when parent country changes
  useEffect(() => {
    if (!country) return;
    void searchState({
      variables: {
        input: {
          generalSearchText: '',
          filterText: country,
          generalSearchType: SearchType.Location,
        },
      },
    });
  }, [country]);


  /* For whatever reason when you set the address fields vales
  manually (formik.setFieldValue) in handleAddressAutocompleteChange formik triggers validation with old
  values, even though the new values are reflected in formik.values. This is needed to make sure we validate the form when
  user selects a value from address autocomplete or switches back and forth beetwen autcomplete-enabled and not autocomplete-enabeld 
  countries */
  useEffect(() => {
    void formik.validateForm();
  }, [formik.values[addressFieldName]]);

  const handleAddressAutocompleteChange = async (key: string) => {
    const { data } = await getLocation({
      variables: {
        query: {
          key,
        },
      },
    });
    if (data) {
      void formik.setFieldValue(stateFieldName, data.locationCoordinatesByKey?.state || '');
      void formik.setFieldValue(addressFieldName, data.locationCoordinatesByKey?.streetAddress || '');
      void formik.setFieldValue(postalCodeFieldName, data.locationCoordinatesByKey?.postalCode || '');
      void formik.setFieldValue(cityFieldName, data.locationCoordinatesByKey?.city || '');
    }
  };

  const resetAddressFields = () => {
    void formik.setFieldValue(stateFieldName, '');
    void formik.setFieldValue(addressFieldName, '');
    void formik.setFieldValue(cityFieldName, '');
    void formik.setFieldValue(postalCodeFieldName, '');
    void formik.setFieldValue('addressAutocompleteLabel', '');
    void formik.setFieldValue('addressAutocompleteKey', '');
  };

  return (
    <>
      <Grid item xs={12}>
        <Autocomplete
          id={getControlId({ type: 'text', name: countryFieldName })}
          onChange={(_e, value) => {
            void formik.setFieldValue(countryFieldName, value?.name || '');
            resetAddressFields();
          }}
          options={countryData?.generalSearch.items || []}
          value={activeCountry}
          loading={countryLoading}
          getOptionLabel={(n) => n.name || ''}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          classes={{
            popupIndicatorOpen: classesAutocomplete.popupIndicatorOpen,
            paper: classesAutocomplete.paper,
            option: classesAutocomplete.option,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              name={countryFieldName}
              label={countryFieldLabel}
              inputProps={{ ...params.inputProps, ...{ autoComplete: 'new-password' } }}
              value={activeCountry?.name}
              onBlur={formik.handleBlur}
              error={formik.touched[countryFieldName] && Boolean(formik.errors[countryFieldName])}
              helperText={formik.touched[countryFieldName] && formik.errors[countryFieldName]}
            />
          )}
        />
      </Grid>

      {activeCountry && activeCountry?.isAutocompleteEnabled && (
        <Grid item xs={12}>
          <Autocomplete
            onChange={(_e, value, reason) => {
              if (reason === 'clear') {
                resetAddressFields();
              } else {
                void handleAddressAutocompleteChange(value?.key as string);
                void formik.setFieldValue('addressAutocompleteLabel', value?.text);
                void formik.setFieldValue('addressAutocompleteKey', value?.key);
              }
            }}
            value={activeSuggestion}
            loading={suggestionsLoading}
            isOptionEqualToValue={(option, value) => option?.text === value?.text}
            onInputChange={(_e, value) => {
              void getSuggestions({
                variables: {
                  query: {
                    text: value,
                    numSuggestions: 5,
                    suggestionType:
                      activeCountry?.name === 'United States of America' ||
                      activeCountry?.name === 'Canada'
                        ? LocationSuggestionType.StreetAddressOnly
                        : LocationSuggestionType.Any,
                  },
                },
              });
            }}
            options={suggestionsData?.locationSuggestions || []}
            filterOptions={(options) => options}
            getOptionLabel={(s) => s?.text || ''}
            renderInput={(props) => (
              <TextField
                {...props}
                inputProps={{ ...props.inputProps, ...{ autoComplete: 'new-password' } }}
                label={addressFieldLabel}
                value={addressAutocompleteLabel}
              />
            )}
          />
        </Grid>
      )}

      {activeCountry && !activeCountry?.isAutocompleteEnabled && (
        <>
          <Grid item xs={12}>
            <Autocomplete
              id={getControlId({ type: 'text', name: stateFieldName })}
              onChange={(_e, value) => {
                void formik.setFieldValue(stateFieldName, value?.name || '');
              }}
              disabled={!country}
              options={stateData?.generalSearch.items || []}
              value={activeState}
              loading={stateLoading}
              classes={{
                popupIndicatorOpen: classesAutocomplete.popupIndicatorOpen,
                paper: classesAutocomplete.paper,
                option: classesAutocomplete.option,
              }}
              getOptionLabel={(n) => n.name || ''}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  name={stateFieldName}
                  label={stateFieldLabel}
                  inputProps={{ ...params.inputProps, ...{ autoComplete: 'new-password' } }}
                  value={activeState?.name}
                  onBlur={formik.handleBlur}
                  error={formik.touched[stateFieldName] && Boolean(formik.errors[stateFieldName])}
                  helperText={formik.touched[stateFieldName] && formik.errors[stateFieldName]}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={getControlId({ type: 'text', name: 'address' })}
              name={addressFieldName}
              label={addressFieldLabel}
              fullWidth
              value={address}
              error={formik.touched[addressFieldName] && Boolean(formik.errors[addressFieldName])}
              helperText={formik.touched[addressFieldName] && formik.errors[addressFieldName]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id={getControlId({ type: 'text', name: cityFieldName })}
              name={cityFieldName}
              label={cityFieldLabel}
              fullWidth
              value={city}
              error={formik.touched[cityFieldName] && Boolean(formik.errors[cityFieldName])}
              helperText={formik.touched[cityFieldName] && formik.errors[cityFieldName]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id={getControlId({ type: 'text', name: 'postalCode' })}
              name={postalCodeFieldName}
              label={postalCodeFieldLabel}
              fullWidth
              value={postalCode}
              error={
                formik.touched[postalCodeFieldName] && Boolean(formik.errors[postalCodeFieldName])
              }
              helperText={formik.touched[postalCodeFieldName] && formik.errors[postalCodeFieldName]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default StandardCountryStateProvinceInput;
